import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { buildPlanFromChallenge } from "../Pages/Purchase/Components/challengePlans";
import { API_BASE_URL } from "../config/api";

const CHALLENGE_API_URL =
  `${API_BASE_URL}landing/challenges/`;

const ChallengeContext = createContext(null);

const ChallengeProvider = ({ children }) => {
  const [challengeData, setChallengeData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchChallengeData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(CHALLENGE_API_URL);

      if (!response.ok) {
        throw new Error(`https error! Status: ${response.status}`);
      }

      const data = await response.json();
      setChallengeData(data);
    } catch (fetchError) {
      console.error("Failed to fetch challenge data:", fetchError);
      setError(fetchError);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChallengeData();
  }, [fetchChallengeData]);

  const plans = useMemo(() => {
    if (!Array.isArray(challengeData)) {
      return [];
    }
    return challengeData
      .map(buildPlanFromChallenge)
      .filter((plan) => plan?.planName);
  }, [challengeData]);

  const challengePlans = useMemo(() => {
    return plans.reduce((acc, plan) => {
      if (plan?.planName) {
        acc[plan.planName] = plan;
      }
      return acc;
    }, {});
  }, [plans]);

  const defaultPlanName = plans[0]?.planName ?? "";

  const getPlanByName = useCallback(
    (planName) => {
      return challengePlans[planName] ?? challengePlans[defaultPlanName];
    },
    [challengePlans, defaultPlanName],
  );

  const value = useMemo(
    () => ({
      challengeData,
      challengePlans,
      plans,
      defaultPlanName,
      getPlanByName,
      isLoading,
      error,
      refetch: fetchChallengeData,
    }),
    [
      challengeData,
      challengePlans,
      plans,
      defaultPlanName,
      getPlanByName,
      isLoading,
      error,
      fetchChallengeData,
    ],
  );

  return (
    <ChallengeContext.Provider value={value}>
      {children}
    </ChallengeContext.Provider>
  );
};

const useChallenge = () => {
  const context = useContext(ChallengeContext);
  if (!context) {
    throw new Error("useChallenge must be used within ChallengeProvider");
  }
  return context;
};

export { ChallengeProvider, useChallenge, buildPlanFromChallenge };
